/**
 * Resolves the BUSINESS ledger for the current account.
 *
 * Only fires the ledger query when `useAccountingAccess()` returns `'granted'`
 * (i.e. the caller is a super-admin). For any normal member the hook returns
 * `{ status: 'no-ledger' }` synchronously without issuing a request – this
 * prevents the 403 that the backend now returns on `listLedgers` for members
 * from surfacing in `App.tsx`'s `QueryCache.onError` Sentry reporter.
 *
 * An account may hold more than one ledger (a business ledger and a trust
 * ledger - s 86(2) of the Legal Practice Act keeps trust money separate). Every
 * business surface (invoices, expenses, the accounting pages) operates on the
 * business ledger only, so resolution must select by `kind`, never by list
 * position: the list has no guaranteed order, and a trust-first response would
 * otherwise expose trust accounts in business flows. `selectBusinessLedger` is
 * the single resolver shared by this hook, the invoice editor, and the expense
 * form. Returns `{ status: 'no-ledger' }` when no business ledger exists.
 *
 * The ready state also exposes every ledger via `ledgers` (for the Business/
 * Trust switch). The accounting page and its drill-in routes pick which ledger
 * they are viewing from the `?ledger` URL param via `useSelectedLedger`, so the
 * top-level `ledgerId`/`kind` (always the business ledger) are not the only
 * ledger a caller may address.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { queryKeys } from '@packages/api';
import { listLedgerAccounts } from '@packages/api/endpoints/ledger.js';
import { ledgersQueryOptions } from './ledger.queries';
import type { Ledger, LedgerAccount, LedgerKind, VatFilingCategory } from '@packages/types';
import { useAuth } from '../../lib/auth/useAuth';
import { useAccountingAccess } from '../../hooks/useAccountingAccess';

/**
 * Pick the account's BUSINESS ledger from a `listLedgers` response array.
 * Returns null when the account has no business ledger (e.g. only a trust
 * ledger, or none at all). Shared by every business-ledger consumer so the
 * kind filter lives in exactly one place.
 */
export function selectBusinessLedger<T extends Pick<Ledger, 'kind'>>(ledgers: T[]): T | null {
  return ledgers.find((ledger) => ledger.kind === 'BUSINESS') ?? null;
}

export type BillLedgerSummary = Pick<
  Ledger,
  | 'id'
  | 'kind'
  | 'baseCurrency'
  | 'openingDate'
  | 'financialYearEndMonth'
  | 'vatFilingCategory'
  | 'openingBalancesPosted'
>;

function orderLedgersForSwitch(ledgers: Ledger[]): BillLedgerSummary[] {
  return ledgers
    .map((ledger) => ({
      id: ledger.id,
      kind: ledger.kind,
      baseCurrency: ledger.baseCurrency,
      openingDate: ledger.openingDate,
      financialYearEndMonth: ledger.financialYearEndMonth,
      vatFilingCategory: ledger.vatFilingCategory,
      openingBalancesPosted: ledger.openingBalancesPosted,
    }))
    .sort((a, b) => {
      if (a.kind === b.kind) return 0;
      return a.kind === 'BUSINESS' ? -1 : 1;
    });
}

// ---------------------------------------------------------------------------
// Ledger selection (the Business/Trust switch)
//
// The accounting page and its drill-in sub-routes (account detail, journal
// form) share which ledger the user is viewing via the `?ledger` URL param.
// These helpers are the single place the param <-> kind <-> ledger mapping
// lives, so every route resolves the selected ledger the same way rather than
// each defaulting to the business ledger. `resolveSelectedLedger` composes
// `selectBusinessLedger`, so the BUSINESS-kind filter stays in one place.
// ---------------------------------------------------------------------------

/** UI-facing ledger identity. Maps to the backend `LedgerKind` via the two
 *  adapters below; the URL param and the switch labels speak this vocabulary. */
export type LedgerSwitchValue = 'business' | 'trust';

const LEDGER_SWITCH_VALUES: ReadonlySet<string> = new Set(['business', 'trust']);

/** Parse the `?ledger` param, defaulting any absent/unknown value to business.
 *  Mirrors the validate-then-default idiom of `isValidTabId` for `?tab`. */
export function parseLedgerParam(raw: string | null): LedgerSwitchValue {
  return raw !== null && LEDGER_SWITCH_VALUES.has(raw) ? (raw as LedgerSwitchValue) : 'business';
}

export function ledgerKindToSwitchValue(kind: LedgerKind): LedgerSwitchValue {
  return kind === 'TRUST' ? 'trust' : 'business';
}

// Internal: maps the UI switch value back to the data-model kind. Used only by
// resolveSelectedLedger below; not part of the public surface.
function switchValueToLedgerKind(value: LedgerSwitchValue): LedgerKind {
  return value === 'trust' ? 'TRUST' : 'BUSINESS';
}

/**
 * Append the ledger context to a link target so navigation carries the current
 * ledger (business is the default and adds nothing). Uses the right separator,
 * so it composes whether or not `path` already has a query string - the single
 * place the `ledger=trust` param rule lives.
 */
export function appendLedgerParam(path: string, value: LedgerSwitchValue): string {
  if (value !== 'trust') return path;
  return `${path}${path.includes('?') ? '&' : '?'}ledger=trust`;
}

/**
 * An account-detail drill-in's transaction window, carried on the `?from=` /
 * `?to=` params (see `account-detail.tsx`'s `parseAccountDateWindow`). `to` is
 * always required - it anchors the trial-balance seed the running balance is
 * computed from. `from` is optional: a balance-sheet drill-in with no known
 * ledger opening date has no lower bound to send (the account is cumulative
 * from inception, matching the account-detail page's own all-time default),
 * so the window covers "everything up to `to`".
 */
export interface LedgerAccountDateWindow {
  from?: string;
  to: string;
}

/**
 * Path to a GL account's detail page, carrying the current ledger context and,
 * optionally, an explicit transaction date window. The route is keyed on the
 * account's `code`, not its id. Every surface that links to an account (trial
 * balance, grouped trial balance, journal legs, income statement / balance
 * sheet / cash flow report rows) goes through here so the ledger param is
 * never dropped and the date window is never string-concatenated at the call
 * site. Omitting `dateWindow` leaves the account-detail page to derive its own
 * default window (today's trial balance), exactly as before this param
 * existed.
 */
export function ledgerAccountPath(
  code: string,
  value: LedgerSwitchValue,
  dateWindow?: LedgerAccountDateWindow,
): string {
  const params = new URLSearchParams();
  if (value === 'trust') params.set('ledger', 'trust');
  if (dateWindow) {
    if (dateWindow.from) params.set('from', dateWindow.from);
    params.set('to', dateWindow.to);
  }
  const qs = params.toString();
  return `/accounting/accounts/${code}${qs ? `?${qs}` : ''}`;
}

/**
 * Apply a ledger selection to the accounting page's URL params: set or clear the
 * `ledger` param (business is the default and carries no param) and drop the
 * ledger-scoped Journals account filter. An account id from the previous ledger
 * does not exist in the newly selected one, so carrying `ledgerAccountIds` across
 * a switch would query the new ledger with foreign ids and show a false empty
 * state. `delete` removes every occurrence of the key, so a multi-account
 * selection is cleared whole. Date, search, and sort params are ledger-agnostic
 * and are preserved.
 */
export function applyLedgerSelection(prev: URLSearchParams, value: LedgerSwitchValue): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (value === 'business') {
    next.delete('ledger');
  } else {
    next.set('ledger', value);
  }
  next.delete('ledgerAccountIds');
  return next;
}

/**
 * Resolve which ledger the `?ledger` selection addresses. Falls back to the
 * business ledger when the requested kind is absent (e.g. `?ledger=trust` on an
 * account with no trust ledger). A business ledger is guaranteed whenever the
 * hook reports `ready` (the hook keys `ready` on `selectBusinessLedger`), so
 * the throw is an unreachable invariant guard, not an expected branch.
 */
export function resolveSelectedLedger(
  ledgers: BillLedgerSummary[],
  selected: LedgerSwitchValue,
): BillLedgerSummary {
  const kind = switchValueToLedgerKind(selected);
  const resolved = ledgers.find((candidate) => candidate.kind === kind) ?? selectBusinessLedger(ledgers);
  if (!resolved) {
    throw new Error('Ready ledger state must include a business ledger');
  }
  return resolved;
}

export type BillLedgerState =
  | { status: 'loading' }
  | { status: 'no-ledger' }
  | {
      status: 'ready';
      ledgerId: string;
      kind: LedgerKind;
      baseCurrency: string;
      openingDate: string | null;
      financialYearEndMonth: number | null;
      vatFilingCategory: VatFilingCategory | null;
      ledgers: BillLedgerSummary[];
      /** True once the CSV conversion entry set has been posted. Controls
       *  whether the opening date is editable and whether the setup-gate
       *  picker is shown on an already-provisioned ledger. */
      openingBalancesPosted: boolean;
    };

/**
 * Shared `listLedgers` read. `useBillLedger`, `useLedgerByKind` and
 * `useBusinessLedgerAccounts` (behind every account picker) key on the same
 * `queryKeys.ledgers(accountId)`, so mounting several of them together (as
 * the bank-account-detail page and its ledger switch do) fires exactly one
 * request. `staleTime` is generous because the ledger list is near-static (an
 * account gains a trust ledger only via an explicit provisioning flow) - this
 * keeps a warmed cache useful for the whole session rather than refetching on
 * every mount. React Query resolves `staleTime` per observer, not per cache
 * entry, so every caller MUST route through this hook rather than opening its
 * own `useQuery` on the same key - a second observer with no `staleTime`
 * refetches on mount regardless of how fresh the shared cache already is.
 *
 * Only fires when accounting access is granted. For normal members the
 * listLedgers route now returns 403, so the query is disabled entirely to
 * keep that 403 out of `App.tsx`'s `QueryCache.onError` Sentry reporter.
 * `extraEnabled` lets a caller AND in its own gate (e.g.
 * `useBusinessLedgerAccounts` passes its `enabled`, so a picker fires only
 * while its host dialog is open) without duplicating the accounting-access check.
 */
export function useLedgersQuery(accountId: string | undefined, extraEnabled = true) {
  const access = useAccountingAccess();
  const isGranted = access === 'granted';

  return useQuery({
    ...ledgersQueryOptions(accountId ?? ''),
    enabled: !!accountId && isGranted && extraEnabled,
  });
}

export interface BusinessLedgerAccounts {
  /** `hidden`: accounting access not granted, the read disabled, or no business ledger. */
  status: 'hidden' | 'loading' | 'error' | 'ready';
  ledger: Ledger | null;
  /** The business ledger's unarchived accounts; empty unless `status` is `ready`. */
  accounts: LedgerAccount[];
}

const NO_ACCOUNTS: LedgerAccount[] = [];

/**
 * The active business ledger's unarchived accounts: the one read behind every
 * account picker. Callers apply their own option filter to `accounts`.
 *
 * Fires no request while `enabled` is false or accounting access is not
 * granted (the ledger-list 403 for members would otherwise reach Sentry). A
 * trust ledger's accounts never appear: the ledger is chosen with
 * `selectBusinessLedger`.
 */
export function useBusinessLedgerAccounts(accountId: string | undefined, enabled = true): BusinessLedgerAccounts {
  const isGranted = useAccountingAccess() === 'granted';
  const ledgersQuery = useLedgersQuery(accountId, enabled);

  const ledger = useMemo(
    () => (ledgersQuery.isError ? null : selectBusinessLedger(ledgersQuery.data?.data ?? [])),
    [ledgersQuery.isError, ledgersQuery.data?.data],
  );

  const accountsQuery = useQuery({
    queryKey: queryKeys.ledgerAccounts(accountId ?? '', ledger?.id ?? ''),
    queryFn: () => listLedgerAccounts(accountId!, ledger!.id),
    enabled: !!accountId && isGranted && enabled && !!ledger,
  });

  const accounts = useMemo(
    () => (accountsQuery.data?.data ?? NO_ACCOUNTS).filter((account) => account.archivedAt === null),
    [accountsQuery.data?.data],
  );

  if (!accountId || !isGranted || !enabled) return { status: 'hidden', ledger: null, accounts: NO_ACCOUNTS };
  if (ledgersQuery.isError) return { status: 'error', ledger: null, accounts: NO_ACCOUNTS };
  if (!ledgersQuery.data) return { status: 'loading', ledger: null, accounts: NO_ACCOUNTS };
  if (!ledger) return { status: 'hidden', ledger: null, accounts: NO_ACCOUNTS };
  if (accountsQuery.isError) return { status: 'error', ledger, accounts: NO_ACCOUNTS };
  if (!accountsQuery.data) return { status: 'loading', ledger, accounts: NO_ACCOUNTS };
  return { status: 'ready', ledger, accounts };
}

export function useBillLedger(): BillLedgerState {
  const { accountId } = useAuth();
  const access = useAccountingAccess();
  const isGranted = access === 'granted';

  const { data, isLoading, isError } = useLedgersQuery(accountId ?? undefined);

  // Non-granted members: return a definite no-ledger state without waiting for
  // any query (there is none). The hook call above still happens so React hooks
  // order is preserved, but with enabled:false it never fires a request.
  if (!isGranted) {
    return { status: 'no-ledger' };
  }

  if (isLoading || (!data && !isError)) {
    return { status: 'loading' };
  }

  // If the query errored or data is absent after loading, treat as no-ledger.
  // The LedgerPage renders an error Alert for both no-ledger cases; a network
  // error will also appear via the global query error handler.
  const ledger = data ? selectBusinessLedger(data.data) : null;
  if (!ledger) {
    return { status: 'no-ledger' };
  }

  return {
    status: 'ready',
    ledgerId: ledger.id,
    kind: ledger.kind,
    baseCurrency: ledger.baseCurrency,
    openingDate: ledger.openingDate,
    financialYearEndMonth: ledger.financialYearEndMonth,
    vatFilingCategory: ledger.vatFilingCategory,
    ledgers: orderLedgersForSwitch(data?.data ?? []),
    openingBalancesPosted: ledger.openingBalancesPosted,
  };
}

// ---------------------------------------------------------------------------
// useLedgerByKind – resolve a ledger by kind (BUSINESS or TRUST)
//
// Shares the same `queryKeys.ledgers` cache as `useBillLedger` so it fires no
// extra network request when both hooks are mounted on an accounting page.
// Unlike `useBillLedger`, this hook can reach a `ready` state even when no
// BUSINESS ledger exists (the trust-first edge), making it the correct
// primitive for:
//   - Bank-account-detail: resolving a trust bank's own ledger.
//   - Account-detail: showing the bank-link affordance on TRUST_BANK_S86_2.
//   - Phase 3/4: trust-ledger creation visibility and kind selector.
//
// Gated on `useAccountingAccess() === 'granted'` so it returns `'no-ledger'`
// without firing a request for members who do not have accounting access,
// matching `useBillLedger`'s access guard.
// ---------------------------------------------------------------------------

export type LedgerByKindState =
  | { status: 'loading' }
  | { status: 'no-ledger' }
  | { status: 'ready'; ledger: BillLedgerSummary };

/**
 * Resolves the ledger of the given kind from the shared `listLedgers` query.
 * Returns `{ status: 'no-ledger' }` synchronously without firing a request
 * when accounting access is not granted.
 */
export function useLedgerByKind(kind: LedgerKind): LedgerByKindState {
  const { accountId } = useAuth();
  const access = useAccountingAccess();
  const isGranted = access === 'granted';

  const { data, isLoading, isError } = useLedgersQuery(accountId ?? undefined);

  if (!isGranted) {
    return { status: 'no-ledger' };
  }

  if (isLoading || (!data && !isError)) {
    return { status: 'loading' };
  }

  const ledger = data ? (data.data.find((l) => l.kind === kind) ?? null) : null;
  if (!ledger) {
    return { status: 'no-ledger' };
  }

  return {
    status: 'ready',
    ledger: {
      id: ledger.id,
      kind: ledger.kind,
      baseCurrency: ledger.baseCurrency,
      openingDate: ledger.openingDate,
      financialYearEndMonth: ledger.financialYearEndMonth,
      vatFilingCategory: ledger.vatFilingCategory,
      openingBalancesPosted: ledger.openingBalancesPosted,
    },
  };
}

export type SelectedLedgerState =
  | { status: 'loading' }
  | { status: 'no-ledger' }
  | {
      status: 'ready';
      /** The ledger the `?ledger` param resolves to (business or trust). */
      selected: BillLedgerSummary;
      /** Every ledger on the account, for the Business/Trust switch. */
      ledgers: BillLedgerSummary[];
      /** `selected.kind` as the UI switch value. */
      switchValue: LedgerSwitchValue;
    };

/**
 * Resolves the ledger the user is currently viewing from the `?ledger` URL
 * param. The accounting page and its drill-in sub-routes (account detail,
 * journal form) all read through this so a trust-context drill-in or journal
 * post addresses the trust ledger rather than silently falling back to the
 * business ledger (the default a bare `useBillLedger` would give).
 */
export function useSelectedLedger(): SelectedLedgerState {
  const ledger = useBillLedger();
  const [searchParams] = useSearchParams();

  if (ledger.status !== 'ready') {
    return ledger;
  }

  const selected = resolveSelectedLedger(ledger.ledgers, parseLedgerParam(searchParams.get('ledger')));
  return {
    status: 'ready',
    selected,
    ledgers: ledger.ledgers,
    switchValue: ledgerKindToSwitchValue(selected.kind),
  };
}
